import React, { useState } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSave,
  faCheckCircle,
  faCalendarAlt,
  faTags,
  faFolder,
  faChartPie,
  faChartLine,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { api } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const Profile = () => {
  const { currentUser, updateMonzoUsername } = useAuth();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: currentUser?.displayName || '',
      monzoUsername: currentUser?.monzoUsername || '',
    },
    validationSchema: Yup.object().shape({
      displayName: Yup.string()
        .min(2, 'Display name is too short')
        .max(50, 'Display name is too long')
        .required('Display name is required'),
      monzoUsername: Yup.string()
        .matches(
          /^[a-zA-Z0-9_-]*$/,
          'Monzo username can only contain letters, numbers, underscores, and hyphens'
        )
        .max(50, 'Monzo username is too long'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await api.put('/users/profile', {
          displayName: values.displayName,
          monzoUsername: values.monzoUsername,
        });

        if (response.data.success) {
          setSuccess('Profile updated successfully!');
          if (values.monzoUsername !== currentUser?.monzoUsername) {
            updateMonzoUsername(values.monzoUsername);
          }
        } else {
          setError(response.data.message || 'Failed to update profile');
        }
      } catch (err) {
        console.error('Error updating profile:', err);
        setError(
          err.response?.data?.message || 'An error occurred while updating your profile'
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const avatarSrc = currentUser?.profilePicture || currentUser?.photoURL;

  return (
    <div className="profile-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Account</p>
          <h1>More</h1>
          <p className="text-muted mb-0">Profile, salary, and household setup.</p>
        </div>
      </header>

      <section className="account-hero">
        <div className="account-hero-main">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="account-avatar" />
          ) : (
            <div className="account-avatar account-avatar-fallback">
              <FontAwesomeIcon icon={faUser} />
            </div>
          )}
          <div>
            <h2 className="h4 mb-1">{currentUser?.displayName || 'User'}</h2>
            <p className="text-muted mb-0">{currentUser?.email || ''}</p>
          </div>
        </div>
      </section>

      <section className="settings-stack mb-4">
        <h2 className="section-title">Shortcuts</h2>
        <div className="settings-links">
          <Link to="/salary" className="settings-link">
            <span className="settings-link-icon">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </span>
            <span className="settings-link-copy">
              <strong>Salary grid</strong>
              <small>Monthly amounts and split overrides</small>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="settings-link-chevron" />
          </Link>
          <Link to="/categories" className="settings-link">
            <span className="settings-link-icon">
              <FontAwesomeIcon icon={faFolder} />
            </span>
            <span className="settings-link-copy">
              <strong>Categories</strong>
              <small>Organize spending</small>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="settings-link-chevron" />
          </Link>
          <Link to="/labels" className="settings-link">
            <span className="settings-link-icon">
              <FontAwesomeIcon icon={faTags} />
            </span>
            <span className="settings-link-copy">
              <strong>Labels</strong>
              <small>Tag expenses for filtering</small>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="settings-link-chevron" />
          </Link>
          <Link to="/statistics" className="settings-link">
            <span className="settings-link-icon">
              <FontAwesomeIcon icon={faChartPie} />
            </span>
            <span className="settings-link-copy">
              <strong>Statistics</strong>
              <small>Balances, settlement, and trends</small>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="settings-link-chevron" />
          </Link>
          <Link to="/analysis" className="settings-link">
            <span className="settings-link-icon">
              <FontAwesomeIcon icon={faChartLine} />
            </span>
            <span className="settings-link-copy">
              <strong>Analysis</strong>
              <small>Drill into category spend by year</small>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="settings-link-chevron" />
          </Link>
        </div>
      </section>

      <Row className="g-4">
        <Col lg={7}>
          <section className="panel">
            <div className="panel-head">
              <h2 className="h5 mb-0">Profile</h2>
            </div>
            <div className="panel-body">
              {error && (
                <Alert
                  variant="danger"
                  className="mb-3"
                  onClose={() => setError(null)}
                  dismissible
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  variant="success"
                  className="mb-3"
                  onClose={() => setSuccess(null)}
                  dismissible
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  {success}
                </Alert>
              )}

              <Form onSubmit={formik.handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={currentUser?.email || ''} disabled />
                  <Form.Text>Linked to Google — not editable here.</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Display name</Form.Label>
                  <Form.Control
                    type="text"
                    name="displayName"
                    value={formik.values.displayName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.displayName && !!formik.errors.displayName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.displayName}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Monzo username</Form.Label>
                  <Form.Control
                    type="text"
                    name="monzoUsername"
                    value={formik.values.monzoUsername}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.monzoUsername && !!formik.errors.monzoUsername}
                    placeholder="your-monzo-username"
                  />
                  <Form.Text>
                    Used for Monzo.me reimbursement links. Find it in the Monzo app profile.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.monzoUsername}
                  </Form.Control.Feedback>
                </Form.Group>

                {formik.values.monzoUsername && (
                  <div className="monzo-preview mb-4">
                    <div className="small text-muted mb-1">Preview</div>
                    <a
                      href={`https://monzo.me/${formik.values.monzoUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      monzo.me/{formik.values.monzoUsername}
                    </a>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !formik.dirty || !formik.isValid}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      Save changes
                    </>
                  )}
                </Button>
              </Form>
            </div>
          </section>
        </Col>

        <Col lg={5}>
          <section className="panel">
            <div className="panel-head">
              <h2 className="h5 mb-0">About Monzo links</h2>
            </div>
            <div className="panel-body">
              <p className="mb-3">
                When someone owes a reclaim, Budgie opens a Monzo.me link for the
                amount entered in the contribution box. The expense title is included
                as the payment reference.
              </p>
              <ol className="mb-0 ps-3 text-muted">
                <li>Open Monzo → profile</li>
                <li>Copy your Monzo.me username</li>
                <li>Paste it above and save</li>
              </ol>
            </div>
          </section>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
